import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://myreadyroom-api-crchhghhctcvgagq.westus2-01.azurewebsites.net';

export default function Scorecard() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [scorecard, setScorecard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    axios.get(`${API_BASE_URL}/api/progress/scorecard/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => {
      setScorecard(r.data.scorecard);
      setLoading(false);
    }).catch(() => {
      setError('Could not load scorecard.');
      setLoading(false);
    });
  }, [sessionId, navigate]);

  const getScoreColor = (score: number) => {
    if (score >= 75) return '#22C55E';
    if (score >= 55) return '#F59E0B';
    return '#EF4444';
  };

  const getVerdict = (score: number, type: string) => {
    if (type === 'presentation') {
      if (score >= 75) return { text: '✓ PRESENTATION READY', color: '#22C55E', bg: '#0A2010' };
      if (score >= 55) return { text: '⚡ NEEDS REFINEMENT', color: '#F59E0B', bg: '#1A1200' };
      return { text: '✗ NOT READY', color: '#EF4444', bg: '#1A0A0A' };
    }
    if (score >= 75) return { text: '✓ INTERVIEW READY', color: '#22C55E', bg: '#0A2010' };
    if (score >= 55) return { text: '⚡ NEEDS REFINEMENT', color: '#F59E0B', bg: '#1A1200' };
    return { text: '✗ NOT READY', color: '#EF4444', bg: '#1A0A0A' };
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.brand}>VIRILITER</p>
          <p style={styles.loading}>Loading scorecard...</p>
        </div>
      </div>
    );
  }

  if (error || !scorecard) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={styles.brand}>VIRILITER</p>
          <p style={styles.loading}>{error || 'Scorecard not found.'}</p>
          <button style={styles.primaryButton} onClick={() => navigate('/history')}>Back to History</button>
        </div>
      </div>
    );
  }

  const verdict = getVerdict(scorecard.overall_score, scorecard.sessionType);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <p style={styles.brand}>VIRILITER</p>

        <div style={styles.sessionMeta}>
          <p style={styles.sessionType}>{scorecard.sessionType === 'presentation' ? 'PRESENTATION PREP' : 'INTERVIEW PREP'}</p>
          <p style={styles.roleTitle}>{scorecard.roleTitle}</p>
          <p style={styles.companyName}>{scorecard.companyName} · {formatDate(scorecard.completedAt)}</p>
        </div>

        {scorecard.session_headline && (
          <p style={styles.scoreHeadline}>{scorecard.session_headline}</p>
        )}

        <p style={styles.overallScore}>
          {scorecard.overall_score}<span style={styles.scoreSuffix}>/100</span>
        </p>

        <div style={{ ...styles.verdictBox, backgroundColor: verdict.bg, borderColor: verdict.color }}>
          <span style={{ ...styles.verdictText, color: verdict.color }}>{verdict.text}</span>
        </div>

        {scorecard.readiness_assessment && (
          <div style={styles.readinessTag}>
            <span style={styles.readinessText}>
              {scorecard.readiness_assessment?.toUpperCase().replace('_', ' ')}
            </span>
          </div>
        )}

        <div style={styles.scoresGrid}>
          {[
            ['Presence', scorecard.score_executive_presence],
            ['Clarity', scorecard.score_strategic_clarity],
            ['Structure', scorecard.score_structured_thinking],
            ['Ownership', scorecard.score_risk_ownership],
          ].map(([label, value]) => value ? (
            <div key={label} style={styles.scoreItem}>
              <p style={{ ...styles.scoreValue, color: getScoreColor(Number(value)) }}>{value}</p>
              <p style={styles.scoreLabel}>{label}</p>
            </div>
          ) : null)}
        </div>

        {scorecard.top_strength && (
          <div style={styles.feedbackBlock}>
            <p style={styles.feedbackLabel}>TOP STRENGTH</p>
            <p style={styles.feedbackText}>{scorecard.top_strength}</p>
          </div>
        )}

        {scorecard.critical_gap && (
          <div style={{ ...styles.feedbackBlock, borderLeftColor: '#EF4444' }}>
            <p style={{ ...styles.feedbackLabel, color: '#EF4444' }}>CRITICAL GAP</p>
            <p style={styles.feedbackText}>{scorecard.critical_gap}</p>
          </div>
        )}

        {scorecard.overall_coaching_feedback && (
          <div style={styles.feedbackBlock}>
            <p style={styles.feedbackLabel}>COACHING FEEDBACK</p>
            <p style={styles.feedbackText}>{scorecard.overall_coaching_feedback}</p>
          </div>
        )}

        <div style={styles.bottomButtons}>
          <button style={styles.primaryButton} onClick={() => navigate(scorecard.sessionType === 'presentation' ? '/presentation-setup' : '/setup')}>
            Practice Again →
          </button>
          <button style={styles.ghostButton} onClick={() => navigate('/history')}>
            Back to History
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#0A0F1E', display: 'flex', justifyContent: 'center', padding: '40px 24px' },
  card: { width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' },
  brand: { color: '#3B82F6', fontSize: '11px', letterSpacing: '6px', fontWeight: '700', textAlign: 'center', margin: 0 },
  loading: { color: '#475569', textAlign: 'center', fontSize: '14px', margin: 0 },
  sessionMeta: { textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px' },
  sessionType: { color: '#3B82F6', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', margin: 0 },
  roleTitle: { color: '#F1F5F9', fontSize: '20px', fontWeight: '800', margin: 0 },
  companyName: { color: '#475569', fontSize: '13px', margin: 0 },
  scoreHeadline: { color: '#94A3B8', fontSize: '14px', textAlign: 'center', margin: 0, lineHeight: '1.5' },
  overallScore: { color: '#F1F5F9', fontSize: '64px', fontWeight: '700', textAlign: 'center', margin: 0, letterSpacing: '-2px' },
  scoreSuffix: { fontSize: '20px', color: '#475569' },
  verdictBox: { borderRadius: '12px', padding: '14px 20px', border: '1px solid', textAlign: 'center', width: '100%', boxSizing: 'border-box' },
  verdictText: { fontSize: '14px', fontWeight: '800', letterSpacing: '1px' },
  readinessTag: { backgroundColor: '#1E3A5F', borderRadius: '20px', padding: '6px 16px', border: '1px solid #3B82F6' },
  readinessText: { color: '#3B82F6', fontSize: '11px', letterSpacing: '2px', fontWeight: '600' },
  scoresGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', width: '100%' },
  scoreItem: { backgroundColor: '#0F172A', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #1E293B' },
  scoreValue: { fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  scoreLabel: { color: '#475569', fontSize: '10px', letterSpacing: '1px', margin: 0 },
  feedbackBlock: { backgroundColor: '#0F172A', borderRadius: '10px', padding: '16px', border: '1px solid #1E293B', borderLeft: '3px solid #3B82F6', width: '100%', boxSizing: 'border-box' },
  feedbackLabel: { color: '#3B82F6', fontSize: '10px', letterSpacing: '2px', fontWeight: '600', margin: '0 0 8px 0' },
  feedbackText: { color: '#94A3B8', fontSize: '14px', lineHeight: '1.6', margin: 0 },
  bottomButtons: { display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' },
  primaryButton: { backgroundColor: '#3B82F6', color: '#fff', padding: '18px', borderRadius: '12px', fontSize: '15px', fontWeight: '700', border: 'none', cursor: 'pointer', width: '100%' },
  ghostButton: { backgroundColor: 'transparent', color: '#475569', padding: '14px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', border: '1px solid #1E293B', cursor: 'pointer', width: '100%' },
};